import { MutableState, Mutator } from 'final-form';
import { addDays, format, parse, parseISO } from 'date-fns';

import { PassengersState } from './store/passengers/reducers';
import { SegmentsState } from './store/segments/reducers';
import { Segment } from './schemas/Segment';
import { PassengerType } from '../enums';
import { Locale, LocationsConfig } from '../types';
import { Prices } from '@features/price-graph';
import { getAirport, getDateWithoutTimezone } from '../Engine/components/FastSearch/utils';
import { getCurrency } from '../cache';
import { PassengerConfigState } from './store/passengersConfig/reducers';
import { Location } from '../LocationAutocomplete/types';
import { getLocale } from '../utils';
import {
	AviaPassengerType,
	AviaSearchParameters,
	DateWithPriceFragment,
	FlightsMinPricesInPeriodParameters,
	PassengerTypeSearchParameter,
	SearchParametersFragment
} from '@websky/graphql';

export const SPECIAL_CATEGORIES = [
	PassengerType.Teenager_GCF,
	PassengerType.YoungAdult,
	PassengerType.RetireeF,
	PassengerType.RetireeM,
	PassengerType.Teenager,
	PassengerType.Teenager_F7,
	PassengerType.Retiree_F7,
	PassengerType.InfantWithSeat_RVT,
	PassengerType.Adult_D2,
	PassengerType.Child_D2,
	PassengerType.Infant_D2,
	PassengerType.InfantWithSeat_D2,
	PassengerType.Retiree_D2,
	PassengerType.AdultWithChild_D2,
	PassengerType.Family_D2,
	PassengerType.Teenager_D2,
	PassengerType.ChildCNN
];

export const setFieldDataMutator: Mutator = (args: any[], state: MutableState<object>) => {
	const [name, data] = args;
	const field = state.fields[name];

	if (field) {
		field.data = { ...field.data, ...data };
	}
};

export const convertPassengersSearchParameters = (
	passengers: SearchParametersFragment['passengers'],
	passengerConfig?: PassengerConfigState
): PassengersState => {
	const result: PassengersState = {};

	passengers.forEach(passenger => {
		if (passenger.extendedPassengerType) {
			if (passengerConfig && !passengerConfig.hasOwnProperty(passenger.extendedPassengerType)) {
				return;
			}

			result[passenger.extendedPassengerType] = passenger.count;

			if (
				passenger.count > 0 &&
				!passengers.find(
					otherPassenger =>
						otherPassenger.passengerType === passenger.passengerType &&
						!otherPassenger.extendedPassengerType
				)
			) {
				result[passenger.passengerType] = 0;
			}
		} else {
			result[passenger.passengerType] = passenger.count;
		}
	});

	return result;
};

export const convertSegmentsSearchParameters = (segments: SearchParametersFragment['segments']): SegmentsState => {
	const simpleRouteSegment: Segment = {
		departure: null,
		arrival: null,
		date: null,
		dateBack: null
	};

	const isRT =
		segments.length === 2 &&
		segments[0].arrival.iata === segments[1].departure.iata &&
		segments[0].departure.iata === segments[1].arrival.iata;

	const isMultiCity = segments.length > 1 && !isRT;

	if (!isMultiCity) {
		simpleRouteSegment.departure = getAirport(segments[0].departure);
		simpleRouteSegment.arrival = getAirport(segments[0].arrival);
		simpleRouteSegment.date = getDateWithoutTimezone(new Date(segments[0].date));
		simpleRouteSegment.dateBack = null;

		if (isRT) {
			simpleRouteSegment.dateBack = getDateWithoutTimezone(new Date(segments[1].date));
		}
	}

	return {
		isMultiCity,
		simpleRouteSegment,
		multiCitySegments: isMultiCity
			? segments.map<Segment>(segment => ({
					departure: segment.departure,
					arrival: segment.arrival,
					date: getDateWithoutTimezone(new Date(segment.date)),
					dateBack: null
			  }))
			: []
	};
};

export const createSearchParameters = (
	passengersState: PassengersState,
	segmentsState: SegmentsState,
	passengersConfig: PassengerConfigState,
	promoCode: string,
	redeemMiles: boolean
): AviaSearchParameters => {
	const passengers: PassengerTypeSearchParameter[] = [];

	for (const passengerType in passengersState) {
		if (passengersState.hasOwnProperty(passengerType) && passengersState[passengerType]) {
			const config = passengersConfig[passengerType];

			passengers.push({
				passengerType: config?.parentCode
					? (config.parentCode as AviaPassengerType)
					: (passengerType as AviaPassengerType),
				extendedPassengerType: config?.parentCode ? (passengerType as AviaPassengerType) : null,
				count: passengersState[passengerType]
			});
		}
	}

	return {
		promotionCode: promoCode,
		ffpMode: redeemMiles,
		currency: getCurrency(),
		segments: (segmentsState.isMultiCity
			? segmentsState.multiCitySegments.sort((a, b) =>
					a.date && b.date ? a.date.getTime() - b.date.getTime() : 0
			  )
			: segmentsState.simpleRouteSegment.dateBack
			? [
					{
						departure: segmentsState.simpleRouteSegment.departure,
						arrival: segmentsState.simpleRouteSegment.arrival,
						date: segmentsState.simpleRouteSegment.date
					},
					{
						departure: segmentsState.simpleRouteSegment.arrival,
						arrival: segmentsState.simpleRouteSegment.departure,
						date: segmentsState.simpleRouteSegment.dateBack
					}
			  ]
			: [segmentsState.simpleRouteSegment]
		).map(segment => {
			let date: string;
			try {
				date = format(segment.date, 'yyyy-MM-dd');
			} catch (e) {}

			return {
				date: date ?? null,
				departure: {
					iata: segment.departure ? segment.departure.iata : ''
				},
				arrival: {
					iata: segment.arrival ? segment.arrival.iata : ''
				}
			};
		}),
		passengers
	};
};

const PASSENGER_TYPES_FOR_SEND = [
	AviaPassengerType.ADT,
	AviaPassengerType.CLD,
	AviaPassengerType.INF,
	AviaPassengerType.INS
];

export const getMinPricesRequestVariables = (
	locations: LocationsConfig,
	passengers: PassengerTypeSearchParameter[]
): FlightsMinPricesInPeriodParameters => {
	const date = format(new Date(), 'yyyy-MM-dd');

	const departureIata = locations.departure?.iata;
	const arrivalIata = locations.arrival?.iata;

	return {
		daysCount: 365,
		passengers: passengers.filter(passenger => PASSENGER_TYPES_FOR_SEND.includes(passenger.passengerType)),
		segments: [
			{
				date,
				departure: { iata: departureIata },
				arrival: { iata: arrivalIata }
			},
			{
				date,
				departure: { iata: arrivalIata },
				arrival: { iata: departureIata }
			}
		]
	};
};

export const getPrices = (dateWithPrices: DateWithPriceFragment[] = [], schedule?: { date: string }[]): Prices => {
	const pricesObject: Prices = {};

	const scheduleMap = new Map<string, { date: string }>();
	schedule?.forEach(item => {
		scheduleMap.set(item.date, item);
	});

	dateWithPrices.forEach(minPrice => {
		if (minPrice) {
			const hasFlightInSchedule = scheduleMap.has(minPrice.date);

			pricesObject[parse(minPrice.date, 'yyyy-MM-dd', new Date()).toISOString()] = {
				price: { ...minPrice.price },
				hasFlight: !!minPrice?.info?.[0] || hasFlightInSchedule
			};
		}
	});

	return pricesObject;
};

export const createFastSearchURL = (parameters: AviaSearchParameters): string => {
	let result = '';

	parameters.segments.forEach(segment => {
		result += `${segment.departure.iata}${segment.arrival.iata}${format(parseISO(segment.date), 'yyyyMMdd')}`;
	});

	parameters.passengers.forEach(({ passengerType, extendedPassengerType, count }) => {
		if (count) {
			result += `${extendedPassengerType ? extendedPassengerType : passengerType}${count}`;
		}
	});

	if (parameters.promotionCode) {
		result += `PROMO${parameters.promotionCode}`;
	}

	if (parameters.ffpMode) {
		result += 'PAYBYMILES';
	}

	if (parameters.currency) {
		result += parameters.currency;
	}

	return result;
};

export type HotelsSupplierData = {
	baseUrl: string;
	destination: Location;
	startDate: Date;
	endDate: Date;
	passengersCount: number;
	passengers: PassengerTypeSearchParameter[];
	iataCode: string;
	utm?: string;
};

const OSTROVOK_MAX_ADULTS = 6;
const OSTROVOK_MAX_CHILDS = 4;

const getChildGuests = (passengers: PassengerTypeSearchParameter[]) => {
	return passengers.reduce<number[]>((ages, passenger) => {
		switch (passenger.passengerType) {
			case AviaPassengerType.INF:
			case AviaPassengerType.INS:
				return [...ages, ...new Array(passenger.count).fill(2)];

			case AviaPassengerType.CLD:
				return [...ages, ...new Array(passenger.count).fill(12)];
		}

		return ages;
	}, []);
};

const ostrovokSupplierLink = (data: HotelsSupplierData) => {
	const url = new URL(data.baseUrl);

	const formatDate = (date: Date) => {
		return format(date, 'dd.MM.yyyy');
	};

	const childGuests = getChildGuests(data.passengers),
		adultsCount = data.passengersCount - childGuests.length;

	let guestsParam = '';

	if (adultsCount > 0 && adultsCount <= OSTROVOK_MAX_ADULTS && childGuests.length <= OSTROVOK_MAX_CHILDS) {
		guestsParam = adultsCount.toString();

		if (childGuests.length > 0) {
			guestsParam += `and${childGuests.join('.')}`;
		}
	}

	url.searchParams.set('q', data.destination.codes[0].code.toString());
	url.searchParams.set('dates', [formatDate(data.startDate), formatDate(data.endDate)].join('-'));

	if (!!guestsParam) {
		url.searchParams.set('guests', guestsParam);
	}

	if (data.utm) {
		url.searchParams.set('utm_source', data.utm);
	}

	url.searchParams.set('lang', getOstrovokISOLocale(data.iataCode));

	return url.toString();
};

export const oneTwoTripSupplierLink = (data: HotelsSupplierData, additionalId: string) => {
	const apiURl = new URL('https://www.onetwotrip.com/_hotels/api/comparer');

	const formatDate = (date: Date) => {
		return format(date, 'yyyy-MM-dd');
	};

	const childs = getChildGuests(data.passengers);

	apiURl.searchParams.set('iata', data.destination.iata);
	apiURl.searchParams.set('date_start', formatDate(data.startDate));
	apiURl.searchParams.set('date_end', formatDate(data.endDate));
	apiURl.searchParams.set('adults', (data.passengersCount - childs.length).toString());

	if (!!childs.length) {
		apiURl.searchParams.set('children', getChildGuests(data.passengers).join(','));
	}

	if (data.utm) {
		apiURl.searchParams.set('utm_source', data.utm);
	}

	return fetch(apiURl.toString())
		.then(response => {
			return response.json();
		})
		.then(data => {
			if (data.result.options[0].url) {
				return data.result.options[0].url.match(/(\/search\/.+)/)?.[0];
			}

			throw 'Incorrect response';
		})
		.then(parsedUrl => {
			const url = new URL(data.baseUrl);

			url.searchParams.set(additionalId, parsedUrl);

			return url.toString();
		})
		.catch(() => {
			return new URL(data.baseUrl).toString();
		});
};

export const generateOfferHotelLink = (
	baseUrl: string,
	segments: Segment[],
	passengersCount: number,
	passengers: PassengerTypeSearchParameter[],
	iataCode: string,
	supplierType: 'OSTROVOK' | 'ONETWOTRIP' = 'OSTROVOK',
	additionalId?: string,
	utm?: string
): Promise<string> => {
	const hotelsData: HotelsSupplierData = {
		baseUrl,
		destination: null,
		startDate: null,
		endDate: null,
		passengersCount,
		passengers,
		iataCode,
		utm
	};

	try {
		const lastSegment = segments[segments.length - 1];

		hotelsData.destination = lastSegment.arrival;

		hotelsData.startDate = lastSegment.date;
		hotelsData.endDate = lastSegment.dateBack;

		if (!hotelsData.endDate) {
			hotelsData.endDate = addDays(hotelsData.startDate, 1);
		}

		if (supplierType === 'OSTROVOK') {
			return Promise.resolve(ostrovokSupplierLink(hotelsData));
		} else if (supplierType === 'ONETWOTRIP') {
			return oneTwoTripSupplierLink(hotelsData, additionalId);
		}
	} catch (e) {
		return null;
	}

	return null;
};

export const getOstrovokISOLocale = (iataCode: string): string => {
	const locale = getLocale();

	if (iataCode === 'RM' && locale === Locale.Armenian) {
		return Locale.Russian;
	}

	switch (locale) {
		case Locale.Chinese:
			return 'zh_CN';
		default:
			return locale;
	}
};
